import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useAuth, useUI } from '../context/AppContext';
import { PATHS, portalPathForRole } from '../navigation/paths';
import {
  isProtectedPath,
  revokePortalAccess,
  hasPortalAccessToken,
  isPortalAccessGranted,
  isExplicitLoginPending,
  grantPortalAccess,
} from '../utils/historySession';

/**
 * Tarayıcı geri/ileri + bfcache sızıntısını yumuşak yönetir.
 * Şifresiz girişte Firebase oturumu korunur; yalnızca portal jetonu düşer.
 * Yeniden SMS istememek için signOut yalnızca korumalı sayfada oturum yoksa yapılır.
 */
const AuthNavigationGuard = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { loggedInUser, isLoading, setLoggedInUser } = useAuth();
  const { isAuthSubmitting } = useUI();
  const handlingRef = useRef(false);

  const softLeavePortal = useCallback(() => {
    revokePortalAccess();
    setLoggedInUser(null);
  }, [setLoggedInUser]);

  const forceLogin = useCallback(async () => {
    softLeavePortal();
    navigate(PATHS.LOGIN, { replace: true });
    /* Firebase oturumu varsa LoginScreen SMS’siz devam ettirir; yoksa tam giriş gerekir */
    if (!auth.currentUser) {
      try {
        await signOut(auth);
      } catch (err) {
        console.warn('Oturum kapatma:', err);
      }
    }
  }, [navigate, softLeavePortal]);

  useEffect(() => {
    const onPopState = () => {
      window.requestAnimationFrame(() => {
        const path = window.location.pathname;
        if (!hasPortalAccessToken()) return;
        if (isProtectedPath(path)) return;
        softLeavePortal();
      });
    };

    const onPageShow = (event) => {
      if (!event.persisted || handlingRef.current) return;

      const path = window.location.pathname;

      if (isProtectedPath(path) && !hasPortalAccessToken()) {
        if (auth.currentUser) {
          grantPortalAccess(auth.currentUser.uid);
          return;
        }
        handlingRef.current = true;
        forceLogin().finally(() => {
          handlingRef.current = false;
        });
        return;
      }

      if (
        (path === PATHS.LANDING || path === PATHS.LOGIN) &&
        hasPortalAccessToken()
      ) {
        softLeavePortal();
      }
    };

    window.addEventListener('popstate', onPopState);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [forceLogin, softLeavePortal]);

  useEffect(() => {
    if (isLoading || isAuthSubmitting || isExplicitLoginPending() || handlingRef.current) {
      return;
    }

    const sessionUid = loggedInUser?.id || auth.currentUser?.uid;

    if (isProtectedPath(pathname) && !isPortalAccessGranted(sessionUid)) {
      if (auth.currentUser && sessionUid) {
        grantPortalAccess(sessionUid);
        return;
      }
      handlingRef.current = true;
      forceLogin().finally(() => {
        handlingRef.current = false;
      });
      return;
    }

    if (pathname === PATHS.LANDING) {
      if (hasPortalAccessToken() || loggedInUser) {
        softLeavePortal();
      }
      return;
    }

    if (pathname === PATHS.LOGIN) {
      if (loggedInUser && isPortalAccessGranted(sessionUid)) {
        navigate(portalPathForRole(loggedInUser.role), { replace: true });
      }
    }
  }, [
    pathname,
    loggedInUser,
    isLoading,
    isAuthSubmitting,
    forceLogin,
    softLeavePortal,
    navigate,
  ]);

  return null;
};

export default AuthNavigationGuard;
